import { useState, useEffect } from "react";
import { importContacts, getGroups, createContact } from "../../mailerApi";
import "./Mailer.css";

function flattenGroups(groups, depth = 0) {
  let result = [];
  for (const g of groups) {
    result.push({ ...g, _depth: depth });
    if (g.children && g.children.length > 0) {
      result = result.concat(flattenGroups(g.children, depth + 1));
    }
  }
  return result;
}

export default function ImportPage() {
  const [file, setFile] = useState(null);
  const [groupId, setGroupId] = useState("");
  const [groups, setGroups] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  // Manual entry state
  const [manualForm, setManualForm] = useState({ email: "", name: "", phone: "", group: "" });
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState("");
  const [manualSuccess, setManualSuccess] = useState("");

  useEffect(() => {
    getGroups()
      .then(d => {
        const tree = Array.isArray(d) ? d : d.results || [];
        setGroups(flattenGroups(tree));
      })
      .catch(() => {});
  }, []);

  async function handleImport() {
    if (!file) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const data = await importContacts(file, groupId || null);
      setResult(data);
      setFile(null);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  }

  async function handleManualSubmit(e) {
    e.preventDefault();
    const email = manualForm.email.trim();
    if (!email) {
      setManualError("ელ.ფოსტა აუცილებელია");
      return;
    }
    setManualLoading(true);
    setManualError("");
    setManualSuccess("");
    try {
      const created = await createContact({
        email,
        name: manualForm.name.trim(),
        phone: manualForm.phone.trim(),
        group: manualForm.group || null,
      });
      const where = manualForm.group
        ? groups.find(g => String(g.id) === String(manualForm.group))?.name
        : null;
      setManualSuccess(
        where
          ? `✓ დაემატა: ${created.email} → ${where}`
          : `✓ დაემატა: ${created.email}`
      );
      // keep selected group, clear other fields for the next entry
      setManualForm({ email: "", name: "", phone: "", group: manualForm.group });
    } catch (err) {
      setManualError(err.message || "შეცდომა დამატებისას");
    }
    setManualLoading(false);
  }

  return (
    <div className="mailer-page">
      <div className="mailer-header">
        <h1>იმპორტი</h1>
      </div>

      {error && <div className="mailer-error">{error}<button onClick={() => setError("")}>×</button></div>}

      <div className="import-section">
        {/* ─── Manual single-contact entry ─── */}
        <div className="import-card">
          <h3 className="import-card-title">ერთი კონტაქტის დამატება</h3>
          <p className="import-card-subtitle">
            თუ ერთი ან რამდენიმე კონტაქტის დამატება გჭირდებათ — ფაილის გარეშე
          </p>

          {manualError && (
            <div className="manual-alert manual-alert-error">
              {manualError}
              <button type="button" onClick={() => setManualError("")}>×</button>
            </div>
          )}
          {manualSuccess && (
            <div className="manual-alert manual-alert-success">
              {manualSuccess}
              <button type="button" onClick={() => setManualSuccess("")}>×</button>
            </div>
          )}

          <form onSubmit={handleManualSubmit} className="manual-form">
            <div className="form-row">
              <div className="form-group">
                <label>ელ.ფოსტა <span className="required-mark">*</span></label>
                <input
                  type="email"
                  value={manualForm.email}
                  onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>სახელი</label>
                <input
                  type="text"
                  value={manualForm.name}
                  onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                  placeholder="გიორგი"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>ტელეფონი</label>
                <input
                  type="text"
                  value={manualForm.phone}
                  onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
                  placeholder="+995 555 12 34 56"
                />
              </div>
              <div className="form-group">
                <label>ჯგუფი / ქვეჯგუფი</label>
                <select
                  value={manualForm.group}
                  onChange={(e) => setManualForm({ ...manualForm, group: e.target.value })}
                >
                  <option value="">არცერთი</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>
                      {g._depth > 0 ? "  ".repeat(g._depth) + "└ " : ""}{g.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              className="btn btn-primary"
              type="submit"
              disabled={manualLoading || !manualForm.email.trim()}
            >
              {manualLoading ? "ემატება..." : "+ კონტაქტის დამატება"}
            </button>
          </form>
        </div>

        <div className="import-divider"><span>ან</span></div>

        {/* ─── CSV / file upload (existing) ─── */}
        <div className="import-card">
          <h3 className="import-card-title">ფაილით ატვირთვა</h3>
          <p className="import-card-subtitle">დიდი რაოდენობის კონტაქტი ერთდროულად</p>

          <div
            className={`drop-zone ${dragOver ? "drag-over" : ""} ${file ? "has-file" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-input").click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files[0])}
            />
            {file ? (
              <div className="file-info">
                <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="#016394" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                <span className="file-name">{file.name}</span>
                <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
              </div>
            ) : (
              <div className="drop-zone-text">
                <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/></svg>
                <p>ჩააგდეთ CSV ფაილი ან დააკლიკეთ</p>
                <span>CSV, TXT, XLSX ფორმატი</span>
              </div>
            )}
          </div>

          <div className="import-options">
            <div className="form-group">
              <label>ჯგუფი (არასავალდებულო)</label>
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">არცერთი</option>
                {groups.map(g => (
                  <option key={g.id} value={g.id}>
                    {g._depth > 0 ? "  ".repeat(g._depth) + "└ " : ""}{g.name}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleImport} disabled={!file || loading}>
              {loading ? "იტვირთება..." : "იმპორტი"}
            </button>
          </div>

          {result && (
            <div className="import-result">
              <h3>იმპორტის შედეგი</h3>
              <div className="result-stats">
                <div className="stat success">
                  <span className="stat-number">{result.created}</span>
                  <span className="stat-label">დამატებული</span>
                </div>
                <div className="stat skipped">
                  <span className="stat-number">{result.skipped}</span>
                  <span className="stat-label">გამოტოვებული</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="import-help">
          <h3>ფორმატის მოთხოვნები</h3>
          <p>CSV ფაილი უნდა შეიცავდეს <strong>email</strong> სვეტს. სურვილისამებრ დაამატეთ <strong>name</strong> სვეტი.</p>
          <div className="csv-example">
            <code>email,name</code>
            <code>user@example.com,გიორგი</code>
            <code>info@company.ge,კომპანია</code>
          </div>
        </div>
      </div>
    </div>
  );
}
